import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import dayjs from "dayjs";

import ChunkUploadFile from "components/ChunkUploadFile";
import Input from "components/Input";
import Select from "components/Select";
import Toggle from "components/Toggle";
import { FR_DEFAULT_ATTACHMENTS_LIST, FR_GENERATED_ATTACHMENT } from "constants/haulpay";
import { FR_CONTRACT_TYPE, FR_STATUS } from "constants/haulpay-broker";
import { UPLOAD_DOCUMENT_URLS } from "constants/routes";
import FRAttachmentItem from "features/main/HaulPay/modules/Broker/FRAttachmentItem";
import FRAttachment from "features/main/HaulPay/modules/Shared/FRAttachment";
import useDeviceType from "hooks/useDeviceType";
import { useUserFeature } from "hooks/useUserFeature";
import { IOption } from "models/Form";
import { IFRAttachment, IFundingRequest, IUpdatedFRAttachment } from "models/HaulPay";
import { getInvoiceForFRAPI } from "services/api/haulpay";
import { selectFactoringProfile, selectUserDetails } from "store/user/selector";
import styles from "./style.module.scss";
import clsx from "clsx";
import DollarInput from "../../../../../../components/DollarInput";

interface IProps {
  isGlobalDisabled: boolean;
  attachments: IFRAttachment[];
  invoiceSentCount: number;
  carrierCanEdit: boolean;
}

interface IVisibleLabel {
  label: string;
  isHide: boolean;
}

const VisibleLabel = ({ label, isHide }: Readonly<IVisibleLabel>) => {
  return !isHide ? (
    <span className={clsx("px-1 text-xs text-placeholder mt-1")}>{label}</span>
  ) : null;
};
export default function StepThree({
  isGlobalDisabled,
  attachments,
  invoiceSentCount,
  carrierCanEdit
}: Readonly<IProps>) {
  const { fundingRequestId } = useParams();
  const { isDesktop } = useDeviceType();
  const userDetails = useSelector(selectUserDetails);
  const factoringProfile = useSelector(selectFactoringProfile);
  const useNewNFRequest = factoringProfile?.use_new_non_factored_funding_requests;
  const enableHoldReviewFundingRequestStatus =
    factoringProfile?.enable_hold_review_funding_request_status;
  const [uploadedFiles, setUploadedFiles] = useState<IFRAttachment[]>([]);
  const { isCarrier, isBroker } = useUserFeature();
  const { register, control, setValue } = useFormContext<IFundingRequest>();
  const contractType = useWatch({ control: control, name: "fundingRequestType" });
  const frStatus = useWatch({ control: control, name: "fundingRequestStatus" });

  const [updatingFile, setUpdatingFile] = useState<IFRAttachment[]>([]);

  const updatedFileId = useMemo(() => {
    if (updatingFile.length > 0) return updatingFile[0].id;
  }, [updatingFile]);
  const [invoiceFR, setInvoiceFR] = useState<FR_GENERATED_ATTACHMENT[]>([]);
  const fundingRequestStatus: IOption[] = useMemo(() => {
    return FR_STATUS.filter((option) => {
      return !(!enableHoldReviewFundingRequestStatus && option.id === 1);
    }).map((option) => {
      if ((useNewNFRequest && (option.id === 6 || option.id === 7)) || option.id === 8) {
        return { ...option, isDisabled: true };
      }
      return option;
    });
  }, [useNewNFRequest, enableHoldReviewFundingRequestStatus]);

  useEffect(() => {
    if (fundingRequestId) {
      setInvoiceFR(
        FR_DEFAULT_ATTACHMENTS_LIST.map((item) => {
          const fetchDocument =
            isBroker && item.value === "invoice_payment_profile"
              ? () => getInvoiceForFRAPI(fundingRequestId, "invoice_payment_profile")
              : () => getInvoiceForFRAPI(fundingRequestId, item.value);
          return {
            ...item,
            fetchDocument
          };
        })
      );
    }
  }, [fundingRequestId, isBroker]);

  useEffect(() => {
    setUploadedFiles(attachments);
  }, [attachments]);

  return (
    <div className={styles.fundingRequestStepThree}>
      <div className={styles.container}>
        {(isDesktop || isCarrier) && (
          <>
            <div className={styles.sectionTwo}>
              <Input
                label={isCarrier ? "Funded At" : "Carrier Funded At"}
                name="carrierFundedAt"
                register={register}
                labelClass="pl-2"
                disabled
              />
            </div>
            {isCarrier ? (
              <DollarInput label="&nbsp;&nbsp;Carrier Amount Funded" name="amountFunded" disabled />
            ) : (
              <>
                <div className={styles.sectionTwo}>
                  <Input
                    label="&nbsp;Internal Funding Request Notes"
                    name="userNotes"
                    register={register}
                    disabled={isGlobalDisabled}
                  />
                  <VisibleLabel label="(Visible to Debtor)" isHide={isCarrier} />
                </div>
                <Input
                  label="&nbsp;&nbsp;Broker Funded At"
                  name="brokerFundedAt"
                  register={register}
                  disabled
                />
              </>
            )}
          </>
        )}
        {!isCarrier ? (
          <Controller
            name="fundingRequestStatus"
            control={control}
            render={({ field: { value = fundingRequestStatus[0], onChange } }) => (
              <Select
                label="&nbsp;&nbsp;Funding Request Status"
                options={fundingRequestStatus}
                value={value}
                onChange={(status) => {
                  if ([FR_STATUS[6].value, FR_STATUS[7].value].includes(status.value)) {
                    const fundingRequestType = FR_CONTRACT_TYPE.find(
                      ({ value }) => status.value === value
                    );
                    fundingRequestType && setValue("fundingRequestType", fundingRequestType);
                  }

                  onChange(status);
                }}
                disabled={
                  isGlobalDisabled ||
                  (["NON_FACTORED_STANDARD", "NON_FACTORED_BILL_OUT"].includes(
                    contractType?.value
                  ) &&
                    !useNewNFRequest) ||
                  [FR_STATUS[6].value, FR_STATUS[7].value].includes(frStatus?.value)
                }
              />
            )}
          />
        ) : null}
        <Input
          label={isCarrier ? "Funding Request Status Notes" : "Status Note"}
          name="statusNote"
          register={register}
          disabled={isGlobalDisabled || isCarrier}
          labelClass="pl-2"
        />

        <div className="flex justify-end col-span-4">
          {factoringProfile?.enable_reserve_holdback_hybrid &&
          !contractType?.value.startsWith("NON_FACTORED") ? (
            <Toggle
              className="items-center self-end"
              name="marginAdvance"
              register={register}
              suffix="Create w/Margin Advance"
              disabled={contractType?.value.startsWith("SELF_FINANCE")}
            />
          ) : null}
        </div>
        {carrierCanEdit && (
          <div className={styles.uploadWrapper}>
            <ChunkUploadFile
              className="desktop:flex desktop:flex-col desktop:gap-4 desktop:text-lg"
              {...(isBroker && { visibility: true })}
              multiple
              urlCallback={
                UPLOAD_DOCUMENT_URLS({
                  factoring_id: userDetails?.factoring_id,
                  funding_request_id: fundingRequestId
                }).FR_ATTACHMENT
              }
              onUploadFinished={(files: any) => {
                const frFiles: IFRAttachment[] = files.map((file: any) => ({
                  id: file.id,
                  filename: file.name,
                  created: dayjs(file.lastModified).format("MM/DD/YYYY HH:mm:ss A"),
                  category: file.categories,
                  downloadUrl: file.preview_url,
                  visibleTo: file.visible_to,
                  size: file.fileSize,
                  type: file.fileType
                }));
                setUploadedFiles([
                  ...uploadedFiles.filter((item: IFRAttachment) => item.id !== updatedFileId),
                  ...frFiles
                ]);
                setValue("attachments", [
                  ...uploadedFiles.filter((item: IFRAttachment) => item.id !== updatedFileId),
                  ...frFiles
                ]);
              }}
              updatedFile={
                updatingFile.map((file) => ({
                  ...file,
                  frId: fundingRequestId
                })) as IUpdatedFRAttachment[]
              }
              setFile={setUpdatingFile}
            />
            <div className={styles.attachmentsList}>
              <span className={styles.title}>Attachments List</span>
              <div className="flex flex-col gap-2">
                {uploadedFiles.length > 0 ? (
                  uploadedFiles.map((file, index) => (
                    <FRAttachmentItem
                      key={file.id}
                      data={file}
                      setAttachments={setUploadedFiles}
                      setFile={setUpdatingFile}
                      name={`attachments[${index}]`}
                    />
                  ))
                ) : (
                  <span>No Attachments</span>
                )}
              </div>
            </div>
          </div>
        )}
        {isBroker && invoiceFR.length ? (
          <div className={styles.totalAttachmentsList}>
            <span className="text-primary desktop:text-text">
              Times Invoice Sent - {invoiceSentCount}
            </span>
            <ul className={styles.invoiceList}>
              {invoiceFR.map((item) => (
                <FRAttachment key={item.id} label={item.label} fetchDocument={item.fetchDocument} />
              ))}
            </ul>
            <span>Total Attachments - {attachments.length}</span>
          </div>
        ) : (
          invoiceFR.length && (
            <div className={styles.totalAttachmentsList}>
              <h4 className="text-placeholder">Uploaded Documents</h4>
              <ul className={styles.invoiceList}>
                {invoiceFR.map((item, index) =>
                  index !== 2 ? (
                    <FRAttachment
                      key={item.id}
                      label={item.label}
                      fetchDocument={item.fetchDocument}
                    />
                  ) : null
                )}
              </ul>
            </div>
          )
        )}
      </div>
    </div>
  );
}
